
import {navigate} from 'gatsby';

import {cookies, CookKey} from './cookies';
import {hasWindow} from './functions';

export const getToken = () => cookies.get(CookKey.Token);

export const setToken = (token: string) => {
	// Let the API determine expiration window.
	// This is much shorter than the # of days
	// that is encoded here.
	cookies.set(CookKey.Token, token, {expires: 90});
	cookies.set(CookKey.BodySpecUser, 'true', {expires: 365});
};

export const clearToken = () => {
	cookies.remove(CookKey.Token);
};

// Sentinel returned in an AccountResponse's `error` field when a user is gated
// into step-up MFA. On a CORRECT password the backend returns this plus a
// `redirectUrl` (and no token) instead of logging the user in.
export const MFA_REQUIRED = 'MFA_REQUIRED';

export type MfaOutcome = 'redirecting' | 'missing-url' | 'none';

// Appends our return-trip context to the backend's step-up start URL so the OTP
// chain can deliver the browser straight to the user's final destination.
// The backend rebuilds the post-MFA target as `https://{host}/{redirect}`, so:
//   redirect -> the FINAL destination path (no leading slash) plus any query
//               params, e.g. `dashboard/settings?tab=security`. Callers pass the
//               already-resolved destination — when the user arrived with a
//               `redirect_url`, that resolved value is what we want, so we land
//               there directly instead of bouncing back through the form.
//               Defaults to the current location for in-app flows (e.g. account
//               settings) where there is nowhere else to go.
//   host     -> the current host including any port, so dev/preview hosts
//               (e.g. `localhost:8000`) come back to themselves.
// Both values are URL-encoded. Exported for unit testing.
export const withStepUpReturn = (redirectUrl: string, finalDestination?: string): string => {
	const sep = redirectUrl.includes('?') ? '&' : '?';
	const here = window.location.pathname + window.location.search;
	const dest = (finalDestination ?? here).replace(/^\/+/, '');
	return `${redirectUrl}${sep}`
		+ `redirect=${encodeURIComponent(dest)}`
		+ `&host=${encodeURIComponent(window.location.host)}`;
};

// Inspects an AccountResponse-like payload for the step-up MFA sentinel and,
// when present, starts the verification flow. Shared by every mutation that
// auto-logs-in (logIn, create, resetPassword, changePassword).
//
// `finalDestination` is the path the user should land on after OTP (callers
// pass their already-resolved redirect target); it defaults to the current
// location. See withStepUpReturn.
//
//   'redirecting' -> a top-level browser navigation into the verification flow
//                    was started. The caller should keep its loading state and
//                    show no error (the page is leaving).
//   'missing-url' -> the sentinel is present but redirectUrl is absent. The
//                    caller should fall back to a generic error rather than
//                    crash or get stuck.
//   'none'        -> not an MFA response; the caller proceeds as normal.
//
// The navigation MUST be a full top-level navigation (not a fetch/XHR) because
// redirectUrl begins a 302 chain into Keycloak.
export const handleMfaStepUp = (
	response: {
		error?: string | null
		redirectUrl?: string | null
	},
	finalDestination?: string,
): MfaOutcome => {
	if (response.error !== MFA_REQUIRED) {
		return 'none';
	}
	if (response.redirectUrl) {
		window.location.assign(withStepUpReturn(response.redirectUrl, finalDestination));
		return 'redirecting';
	}
	return 'missing-url';
};

export const createRedirect = (base: string, target?: string) => {
	if (!target) {
		if (!hasWindow()) {
			return base;
		}

		// If url = https://xyz/123?ab=1#h
		// Remove : https://xyz
		const origin = window.location.origin.length;
		target = window.location.href.substring(origin) || '';
	}

	// Replace any leading slashes
	if (target.charAt(0) === '/') {
		target = target.replace(/^\/+/, '');
	}

	return `${base}?redirect_url=${encodeURIComponent(target)}`;
};

export const applyRedirect = (fallback: string) => {
	const redirect = new URLSearchParams(location.search).get('redirect_url');
	if (!redirect) {
		navigate(fallback);
		return;
	}

	const decoded = decodeURIComponent(redirect);
	navigate(`/${decoded}`);
};
